import {
  convertMemberAliasToResponse,
  Id,
  Member,
  MemberAlias,
  MembersData,
} from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import keys from 'chat/shared/queries/keys'
import { produce } from 'immer'
import { convertMemberAliasesToMembers } from '../util'

type Variables = {
  conversationId: Id
  members: Member[]
  memberAliases: MemberAlias[]
}

export function useUpdateMembers() {
  const { server } = useServer()
  const client = useQueryClient()

  return useMutation({
    mutationFn: updateMembers,
    onMutate: async ({ conversationId, members, memberAliases }) => {
      return updateCache(conversationId, members, memberAliases)
    },
    onError: (_, { conversationId }, previousData: MembersData | undefined) => {
      restoreCache(conversationId, previousData).catch(e => null)
    },
    onSettled: async (data, error, { conversationId }) => {
      client
        .invalidateQueries({ queryKey: keys.membersWithAlias(conversationId) })
        .catch(e => null)
      client
        .invalidateQueries({ queryKey: keys.members(conversationId, []) })
        .catch(e => null)
    },
  })

  async function updateCache(
    conversationId: string,
    members: Member[],
    memberAliases: MemberAlias[]
  ) {
    const key = keys.membersWithAlias(conversationId)
    const data = client.getQueryData<MembersData>(key)

    if (!data) {
      return
    }

    await client.cancelQueries({ queryKey: key })
    const aliasMembers = convertMemberAliasesToMembers(memberAliases)
    client.setQueryData<MembersData>(key, members.concat(aliasMembers))

    return data
  }

  async function restoreCache(
    conversationId: string,
    context: MembersData | undefined
  ) {
    if (!context) {
      return
    }

    const key = keys.membersWithAlias(conversationId)

    if (!client.getQueryData(key)) {
      return
    }

    await client.cancelQueries({ queryKey: key })
    client.setQueryData<MembersData>(key, context)
  }

  function updateMembers({
    conversationId,
    members,
    memberAliases,
  }: Variables): Promise<void> {
    return server.put(
      `/v2/chat/conversations/${conversationId}/members`,
      getBody({ members, memberAliases })
    )
  }

  function getBody({
    members,
    memberAliases,
  }: {
    members: Member[]
    memberAliases: MemberAlias[] | undefined
  }) {
    const memberAlias = memberAliases?.map(a => convertMemberAliasToResponse(a))
    return {
      user_ids: members.map(m => parseInt(m.id)),
      member_alias: memberAlias,
    }
  }
}

type MemberAdminVariables = {
  conversationId: Id
  memberId: Id
  adminFlag: boolean
}

export function useUpdateMemberAdminStatus() {
  const { server } = useServer()
  const client = useQueryClient()

  return useMutation({
    mutationFn: updateMemberAdminStatus,
    onSuccess: async (_, { conversationId, memberId, adminFlag }) => {
      const key = keys.membersWithAlias(conversationId)
      const data = client.getQueryData<MembersData>(key)
      if (!data) {
        return
      }
      const index = data.findIndex(m => !m.isAlias && m.id === memberId)
      if (index < 0) {
        return
      }

      await client.cancelQueries({ queryKey: key })
      client.setQueryData<MembersData>(
        key,
        produce(data, updatedData => {
          updatedData[index].isAdmin = adminFlag
        })
      )
    },
  })

  function updateMemberAdminStatus({
    conversationId,
    memberId,
    adminFlag,
  }: MemberAdminVariables): Promise<void> {
    return server.put(
      `/v2/chat/conversations/${conversationId}/members/${memberId}/update_admin_status?adminFlag=${adminFlag}`,
      {}
    )
  }
}
